/*
The PostForm component provides a form in a modal form for creating and editing posts in the user dashboard.
It collects title, content, optional image URLs, tags, and lets the user choose to post anonymously or not.
*/
import { useState, useEffect } from "react";
import { useAuth } from "../contexts/AuthContext";
import { createNewPost, updatePost } from "../services/postService";
import "../styles/PostForm.css";
import { trackPostCreated } from "../services/analyticsService";

function PostForm({
  onClose,
  onPostCreated,
  editingPost = null,
  isEditing = false,
}) {
  const { currentUser } = useAuth();
  const [formData, setFormData] = useState({
    title: "",
    content: "",
    imageUrl: "",
  });
  const [tags, setTags] = useState([]);
  const [tagInput, setTagInput] = useState("");
  const [isAnonymous, setIsAnonymous] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState("");

  // Populate form when editing
  useEffect(() => {
    if (isEditing && editingPost) {
      setFormData({
        title: editingPost.title || "",
        content: editingPost.content || "",
        imageUrl: editingPost.imageUrl || "",
      });
      setTags(editingPost.tags || []);
      setIsAnonymous(editingPost.isAnonymous || false);
    }
  }, [isEditing, editingPost]);

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData((prev) => ({
      ...prev,
      [name]: value,
    }));
  };

  const handleTagInputChange = (e) => {
    setTagInput(e.target.value);
  };

  const handleTagInputKeyPress = (e) => {
    if (e.key === "Enter" || e.key === ",") {
      e.preventDefault();
      addTag();
    }
  };

  const handleAnonymousChange = (e) => {
    setIsAnonymous(e.target.checked);
  };

  const addTag = () => {
    const newTag = tagInput.trim();
    if (
      newTag &&
      !tags.map((tag) => tag.toLowerCase()).includes(newTag.toLowerCase())
    ) {
      setTags([...tags, newTag]);
      setTagInput("");
    }
  };

  const removeTag = (indexToRemove) => {
    setTags(tags.filter((_, index) => index !== indexToRemove));
  };

  const handleSubmit = async (e) => {
    // Prevent multiple submissions
    if (isLoading) return;

    e.preventDefault();

    // User validation for later implementation of non-authenticated browsing
    if (!currentUser) {
      setError(
        `You must be logged in to ${isEditing ? "edit" : "create"} a post`,
      );
      return;
    }

    if (!formData.title.trim() || !formData.content.trim()) {
      setError("Title and content are required");
      return;
    }

    setIsLoading(true);
    setError("");

    try {
      let success = false;

      if (isEditing && editingPost) {
        // Update existing post
        success = await updatePost(editingPost.id, {
          title: formData.title.trim(),
          content: formData.content.trim(),
          imageUrl: formData.imageUrl.trim(),
          tags: tags,
          isAnonymous: isAnonymous,
        });
      } else {
        // Create new post
        const postId = await createNewPost(
          formData.title.trim(),
          formData.content.trim(),
          formData.imageUrl.trim(),
          tags,
          isAnonymous,
        );
        success = !!postId;
      }

      if (success) {
        // Reset form when creating new post
        if (!isEditing) {
          setFormData({
            title: "",
            content: "",
            imageUrl: "",
          });
          setTags([]);
          setTagInput("");
          setIsAnonymous(false);
        }

        // Notify app component
        if (onPostCreated) {
          onPostCreated();
        }
        if (onClose) {
          onClose();
        }
        trackPostCreated({ isAnonymous, tagCount: tags.length });
      } else {
        setError(
          `Failed to ${isEditing ? "update" : "create"} post. Please try again.`,
        );
      }
    } catch (error) {
      console.error(
        `Error ${isEditing ? "updating" : "creating"} post:`,
        error,
      );
      setError(
        `An error occurred while ${
          isEditing ? "updating" : "creating"
        } the post`,
      );
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <div className="post-form-overlay">
      <div className="post-form-container">
        <div className="post-form-header">
          <h2>{isEditing ? "Edit Post" : "Create New Post"}</h2>
          <button className="close-button" onClick={onClose}>
            ✕
          </button>
        </div>

        <form onSubmit={handleSubmit} className="post-form">
          {error && <div className="error-message">{error}</div>}

          <div className="form-group">
            <label htmlFor="title">Title *</label>
            <input
              type="text"
              id="title"
              name="title"
              value={formData.title}
              onChange={handleChange}
              placeholder="Enter post title..."
              maxLength={100}
              required
            />
            <small>{formData.title.length}/100 characters</small>
          </div>

          <div className="form-group">
            <label htmlFor="content">Content *</label>
            <textarea
              id="content"
              name="content"
              value={formData.content}
              onChange={handleChange}
              placeholder="Share your language learning experience, tips, or questions..."
              rows={8}
              maxLength={2000}
              required
            />
            <small>{formData.content.length}/2000 characters</small>
          </div>

          <div className="form-group">
            <label htmlFor="imageUrl">Image URL (optional)</label>
            <input
              type="url"
              id="imageUrl"
              name="imageUrl"
              value={formData.imageUrl}
              onChange={handleChange}
              placeholder="https://example.com/image.jpg"
            />
            <small>Add an image to make your post more engaging</small>
          </div>

          <div className="form-group">
            <label htmlFor="tags">Tags</label>
            <div className="tags-input-container">
              <input
                type="text"
                id="tags"
                value={tagInput}
                onChange={handleTagInputChange}
                onKeyDown={handleTagInputKeyPress}
                placeholder="Add tags (e.g., french, grammar, pronunciation)..."
                className="tag-input"
              />
              <button
                type="button"
                onClick={addTag}
                className="add-tag-button"
                disabled={!tagInput.trim()}
              >
                Add
              </button>
            </div>
            <div className="tags-display">
              {tags.map((tag, index) => (
                <span key={index} className="tag-bubble">
                  {tag}
                  <button
                    type="button"
                    onClick={() => removeTag(index)}
                    className="remove-tag-button"
                  >
                    ✕
                  </button>
                </span>
              ))}
            </div>
            <small>
              Press Enter or comma to add tags. Tags help others find your post.
            </small>
          </div>

          <div className="form-group">
            <label className="checkbox-label">
              <input
                type="checkbox"
                checked={isAnonymous}
                onChange={(e) => handleAnonymousChange(e)}
              />
              Post anonymously
            </label>
            <small>
              Your post will be shown as "By: Anonymous" instead of your name.
            </small>
          </div>

          <div className="form-actions">
            <button
              type="button"
              className="cancel-button"
              onClick={onClose}
              disabled={isLoading}
            >
              Cancel
            </button>
            <button
              type="submit"
              className="submit-button"
              disabled={isLoading}
            >
              {isLoading
                ? isEditing
                  ? "Updating..."
                  : "Creating..."
                : isEditing
                  ? "Update Post"
                  : "Create Post"}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
}

export default PostForm;
